<!--
  The page for viewing and editing synonyms
-->

<template>
  <el-form
    ref="form"
    v-loading="state.loading"
    :model="viewObj"
    label-width="120px"
    :hide-required-asterisk="true">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home', query: {view: view, compare: compare} }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-if="skill==='global'">
        <router-link :to="{ name: 'global', query: {view: view, compare: compare} }">
          Global items
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item v-else>
        <router-link :to="{ name: 'skill', params: {skill: skill}, query: {view: view, compare: compare} }">
          Skill summary
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{synonymId==='new'?'Create synonym':'Synonym details'}}</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title
      :skill="skill"
      v-model="viewObj"
      id-field="synonym"
      :object-id="synonymId"
      :disabled="viewObj.source!=='list'"
      :existing="skillObj.data.synonyms">
      Synonym: {{viewObj.name}}
    </main-title>
    <view-selector
      :view="view"
      :compare="compare"
      @update="changeView"
      :object="viewObj"
      :publishable="skill === 'global'"
      :discrete="skill !== 'global' && skillObj.data.discrete_publish"
      :new-item="synonymId==='new' || special && !timestamp"
      name="entity"
      @save="save"
      @delete="remove"
      @test="test"
      @untest="untest"
      @publish="publish"
      @unpublish="unpublish"
      @unprod="unprod"
      @reject="reject"
      @approve="approve"
      @disapprove="disapprove"
      @restore="restore"
      ></view-selector>
    <p>
      <subtitle>Synonym</subtitle>
      <!-- only list-type can be managed -->
      <el-alert
        v-if="viewObj.source!=='list'"
        type="warning"
        show-icon
        :closable="false">
        <template slot="title">Unmanageable <em>synonym</em> type</template>
      </el-alert>
      <!-- name input -->
      <comparator :view="viewObj" :compare="compareObj"
        prop="name"
        :required="true"
        :rules="AcceptableValidator">
        <template v-slot="{ value, compare, prop, attrs, status }">
          <el-form-item :class="'comparable-' + status"
            :prop="prop"
            v-bind="attrs"
            label="Name">
            <el-input v-model="value.name"
              :disabled="compare || special || viewObj.source!=='list'"></el-input>
          </el-form-item>
        </template>
      </comparator>
    </p>
    <p>
      <subtitle>Words
        <template slot="tooltip">
          Configure the words that match the synonym.
        </template>
      </subtitle>
      <comparator :view="viewObj" :compare="compareObj"
        prop="source">
        <template v-slot="{ value, other, status, view }">
          <!-- the editor for a list-type synonym -->
          <div v-if="value.source==='list'">
            <fieldset>
              <legend>Synonyms</legend>
              <el-row>
                <comparator :view="value.synonyms"
                  :compare="other && other.synonyms">
                  <template v-slot="{ value: v, index }">
                    <!-- the synonym inputs -->
                    <el-col :span="12" style="padding: 5px">
                      <comparable prop
                        :required="true"
                        :rules="SynonymValidator"
                        @input="v => $set(value.synonyms, index, v)">
                        <template
                          v-slot="{ view, compare, attrs, listeners, status }">
                          <el-form-small-item :class="'comparable-' + status"
                            :prop="view && 'synonyms.' + index"
                            v-bind="attrs">
                            <el-button-group>
                              <el-input :ref="view && 'synonyms-' + index"
                                :value="v"
                                v-on="listeners"
                                :disabled="compare"></el-input>
                              <el-button
                                icon="el-icon-delete"
                                @click="view && deleteSynonym(index)"
                                :disabled="compare"
                                :tabindex="-1"></el-button>
                            </el-button-group>
                          </el-form-small-item>
                        </template>
                      </comparable>
                    </el-col>
                  </template>
                </comparator>
                <!-- a new synonym input -->
                <el-col v-if="view && !$route.query.view"
                  :span="12" style="padding: 5px">
                  <el-form-small-item
                    prop="synonyms"
                    :rules="EmptyValidator">
                    <el-button-group>
                      <el-input v-model="newSynonym"></el-input>
                      <el-button
                        icon="el-icon-circle-plus-outline"
                        :disabled="true"
                        :tabindex="-1"></el-button>
                    </el-button-group>
                  </el-form-small-item>
                </el-col>
              </el-row>
            </fieldset>
          </div>
        </template>
      </comparator>
    </p>
  </el-form>
</template>

<script>
import State from '../../stores/state.js'
import { PreSynonymValidator, SynonymValidator, EmptySynonymValidator,
  AcceptableValidator } from '../../utils/validators'
import ViewSelector from '../ViewSelector.vue'

// validator to check if there's at least one synonym
const EmptyValidator = {
  validator (rule, value, callback) {
    callback(
      !value.length ? new Error('synonym required') : undefined)
  },
  trigger: 'submit',
}

export default {
  name: 'SynonymPage',
  components: {
    ViewSelector,
  },
  props: {
    skill: String,
    synonym: String,
    view: String,
    compare: String,
  },
  data () {
    return {
      state: State.state,
      synonymId: null, // "new" for a new synonym, then updated on creation
      skillObj: State.empty,
      viewObj: {
        source: 'list',
        name: '',
        synonyms: [],
      },
      compareObj: undefined,
      newSynonym: '', // the field for a new synonym
      AcceptableValidator,
      SynonymValidator: [
        PreSynonymValidator, SynonymValidator, EmptySynonymValidator],
      EmptyValidator,
    }
  },
  inject: ['onerror'],
  // load data
  created () {
    this.synonymId = this.synonym
    Promise.all([
      this.loadSkill(),
      this.loadView(),
      this.loadCompare(),
    ]).finally(() => {
      this.$anchorsLoaded()
    })
  },
  computed: {
    // shortcuts
    status () { return this.viewObj.status || null },
    timestamp () { return this.viewObj.timestamp || null },
    published () { return this.viewObj.published || null },
    approved () { return this.viewObj.approved || null },
    special () {
      return this.viewObj.synonym
        ? this.viewObj.synonym.length !== 32 : false
    },
  },
  watch: {
    // user connected, reload everything
    'state.user' () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // skill changed, reload everything
    skill () {
      this.loadSkill()
      this.loadView()
      this.loadCompare()
    },
    // synonym change, reload synonym data
    synonym () {
      if (this.synonymId === this.synonym) return
      this.synonymId = this.synonym
      this.loadView()
      this.loadCompare()
    },
    // view change, reload view data
    view () {
      this.loadSkill()
      this.loadView()
    },
    // compare change, reload compare data
    compare () { this.loadCompare() },
    // create a new synonym input
    newSynonym (val) {
      if (!val) return
      this.viewObj.synonyms.push(val)
      // each character would create a new input
      // so needs to give back the focus to newly created input
      // and feed it back with the missed characters
      this.$nextTick(() => {
        this.viewObj.synonyms[this.viewObj.synonyms.length - 1] = this.newSynonym
        this.$refs['synonyms-' + (this.viewObj.synonyms.length - 1)].focus()
        this.newSynonym = ''
      })
    },
  },
  methods: {
    // load skill data
    loadSkill () {
      let skill = this.skill, view = this.view
      return State.getSkill(skill, view).then(data => {
        if (skill !== this.skill || view !== this.view) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    // load view synonym data
    loadView () {
      if (this.synonymId === 'new') {
        this.viewObj = {
          source: 'list',
          name: '',
          synonyms: [],
        }
        return Promise.resolve()
      } else {
        let skill = this.skill, synonym = this.synonymId, view = this.view
        return State.getSynonym(skill, synonym, view).then(data => {
          if (skill !== this.skill || synonym !== this.synonymId ||
            view !== this.view) return
          this.viewObj = data
        }).catch(onerror)
      }
    },
    // load compare synonym data
    loadCompare () {
      if (this.synonymId === 'new' || !this.compare) {
        this.compareObj = undefined
        return Promise.resolve()
      } else {
        let skill = this.skill, synonym = this.synonymId, view = this.compare
        return State.getSynonym(skill, synonym, view).then(data => {
          if (skill !== this.skill || synonym !== this.synonymId ||
            view !== this.compare) return
          this.compareObj = data
        }).catch(onerror)
      }
    },
    // delete a synonym input
    deleteSynonym (index) {
      this.viewObj.synonyms.splice(index, 1)
    },
    // delete synonym
    remove () {
      State.removeSynonym(this.skill, this.synonymId).then(() => {
        this.$router.replace('/skills/' + this.skill +
          '/synonyms/new')
      }).catch(this.onerror)
    },
    // save synonym
    save () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          this.loading = true
          let promise
          if (this.synonymId === 'new') {
            promise = State.createSynonym(this.skill, this.viewObj, this.viewObj.synonym)
              .then(data => {
                this.$message({
                  message: 'Synonym created',
                  type: 'success',
                })
                this.synonymId = data.synonym
                this.viewObj = data
                this.$router.replace('/skills/' + this.skill +
                '/synonyms/' + this.synonymId)
              })
          } else {
            promise = State.saveSynonym(this.skill, this.synonymId, this.viewObj)
              .then(data => {
                this.$message({
                  message: 'Synonym saved',
                  type: 'success',
                })
                this.viewObj = data
              })
          }
          promise.catch(this.onerror).finally(() => { this.loading = false })
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // untest synonym
    untest () {
      State.untestSynonym(this.skill, this.synonymId).then(() => {
        this.$message({
          message: 'Test cleared',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // test synonym
    test () {
      State.testSynonym(this.skill, this.synonymId).then(() => {
        this.$message({
          message: 'Synonym in test',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update synonym data with the field from ppublished, ...
    updateSynonym (data) {
      if (!data || !data.synonym) return
      for (let field in data) this.viewObj[field] = data[field]
    },
    // unpublish synonym
    unpublish () {
      State.unpublishSynonym(this.skill, this.synonymId, this.published).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: 'Synonym unpublished',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // unprod synonym
    unprod () {
      State.publishSynonym(this.skill, this.synonymId, false).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: 'Synonym unproded',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // publish synonym
    publish () {
      State.publishSynonym(this.skill, this.synonymId, this.timestamp).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: 'Synonym published',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // disapprove synonym
    disapprove () {
      State.disapproveSynonym(this.skill, this.synonymId, this.approved).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: 'Synonym disapproved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // reject synonym
    reject () {
      State.rejectSynonym(this.skill, this.synonymId, this.published).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: 'Synonym rejected',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // approve synonym
    approve () {
      State.approveSynonym(this.skill, this.synonymId, this.published).then(data => {
        this.updateSynonym(data)
        this.$message({
          message: data.published ? 'Synonym approved' : 'Deletion approved',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // restore synonym
    restore (version) {
      let timestamp = version === 'pub' ? this.published : this.approved
      let name = version === 'pub' ? 'Published' : 'Approved'
      console.log('restore', version, timestamp, name)
      State['restore' + name + 'Synonym'](this.skill, this.synonymId, timestamp).then(() => {
        this.$message({
          message: name + ' synonym restored',
          type: 'success',
        })
      }).catch(this.onerror)
    },
    // update view or compare
    changeView (view, compare) {
      this.$router.push({ query: { view: view, compare: compare } })
    },
  },
}
</script>
